import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Panel, TokenLogo, VerifiedBadge, StarButton } from "../components/common/Panel";
import { CopyBtn, Row } from "../components/common/Detail";
import ContractConsole from "../components/common/ContractConsole";
import { Loading, NotFound } from "./BlockDetail";
import { getAddress, getAddressTokens, getContract, getAddressTxs, getTokens, getContractEvents, submitTagRequest } from "../lib/api";
import { useAdmin } from "../hooks/useAdmin";
import { fmtEth, fmtNum, shorten, timeAgo, methodOf, methodClass, CHAIN } from "../lib/format";
import { useWatchlist } from "../hooks/useWatchlist";

const PAGE_SIZE = 25;
const METHODS = ["all", "Transfer", "TransferFrom", "Approve", "Mint", "Call"];

function DirTag({ dir }) {
  const map = {
    in: { label: "IN", cls: "pill-green", icon: "fa-arrow-down" },
    out: { label: "OUT", cls: "pill-red", icon: "fa-arrow-up" },
    self: { label: "SELF", cls: "pill-muted", icon: "fa-rotate" },
  };
  const m = map[dir] || map.self;
  return <span className={`pill ${m.cls}`}><i className={`fa-solid ${m.icon}`} /> {m.label}</span>;
}

export default function AddressDetail() {
  const { addr } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [holdings, setHoldings] = useState([]);
  const [contract, setContract] = useState(null);
  const [tab, setTab] = useState("txs");
  const [ctab, setCtab] = useState("code");
  const { isStarred, toggle } = useWatchlist();

  // wallet activity + filters
  const [txData, setTxData] = useState(null);
  const [page, setPage] = useState(1);
  const [txLoading, setTxLoading] = useState(false);
  const [dir, setDir] = useState("");
  const [method, setMethod] = useState("all");
  const [tokenFilter, setTokenFilter] = useState("");
  const [tokenOpts, setTokenOpts] = useState([]);
  // contract events
  const [events, setEvents] = useState(null);
  const [evLoading, setEvLoading] = useState(false);
  // public name tag
  const { admin } = useAdmin();
  const [tagOpen, setTagOpen] = useState(false);
  const [tagLabel, setTagLabel] = useState("");
  const [tagContact, setTagContact] = useState("");
  const [tagNote, setTagNote] = useState("");
  const [tagBusy, setTagBusy] = useState(false);
  const [tagMsg, setTagMsg] = useState(null);
  const [tagErr, setTagErr] = useState("");

  useEffect(() => {
    setData(null); setErr(false); setHoldings([]); setContract(null);
    setTab("txs"); setCtab("code"); setPage(1); setTxData(null);
    setDir(""); setMethod("all"); setTokenFilter("");
    setTagOpen(false); setTagMsg(null); setTagErr(""); setTagLabel("");
    getAddress(addr).then(setData).catch(() => setErr(true));
    getAddressTokens(addr).then((d) => setHoldings(d.holdings || [])).catch(() => {});
    getContract(addr).then((c) => { if (c.isContract) setContract(c); }).catch(() => {});
    getTokens().then((d) => setTokenOpts(d.tokens || [])).catch(() => {});
  }, [addr]);

  useEffect(() => {
    if (tab !== "txs") return;
    setTxLoading(true);
    getAddressTxs(addr, { page, limit: PAGE_SIZE, direction: dir, method, token: tokenFilter })
      .then(setTxData)
      .catch(() => setTxData({ transactions: [], hasMore: false, scanComplete: true }))
      .finally(() => setTxLoading(false));
  }, [addr, tab, page, dir, method, tokenFilter]);

  useEffect(() => {
    if (tab !== "contract" || ctab !== "events") return;
    setEvLoading(true);
    getContractEvents(addr, { page: 1, limit: 50 })
      .then(setEvents)
      .catch(() => setEvents({ events: [], total: 0 }))
      .finally(() => setEvLoading(false));
  }, [addr, tab, ctab]);

  const resetAnd = (fn) => { setPage(1); fn(); };

  const submitTag = async (e) => {
    e.preventDefault();
    setTagBusy(true); setTagErr(""); setTagMsg(null);
    try {
      const r = await submitTagRequest({ address: data.address, label: tagLabel, contact: tagContact, note: tagNote });
      setTagMsg(r);
      setTagLabel(""); setTagContact(""); setTagNote("");
    } catch (err) {
      setTagErr(err?.response?.data?.detail || "Failed to submit request");
    } finally { setTagBusy(false); }
  };

  if (err) return <NotFound label="Address" navigate={navigate} />;
  if (!data) return <Loading />;

  const hasMore = txData?.hasMore;
  const isVerifiedContract = contract?.verified;

  return (
    <div className="fade-in" data-testid="address-detail-page">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="nav-link" data-testid="back-btn"><i className="fa-solid fa-arrow-left" /></button>
        <div className="flex-1 min-w-0">
          <span className="eyebrow" style={{ color: "var(--amber)" }}>{data.isContract ? "Contract" : "Address"}</span>
          <h1 className="font-display text-[18px] font-extrabold mt-1 mono break-all flex items-center gap-2 flex-wrap">
            {data.address}
            {isVerifiedContract && <VerifiedBadge />}
          </h1>
          {data.publicTag && (
            <div className="mt-1.5 flex items-center gap-2" data-testid="address-public-tag">
              <span className="pill pill-amber" style={{ fontSize: 12 }}><i className="fa-solid fa-tag mr-1" />{data.publicTag}</span>
              <span className="text-[10.5px]" style={{ color: "var(--muted-2)" }}>public name tag</span>
            </div>
          )}
        </div>
        <StarButton starred={isStarred(data.address)} onToggle={() => toggle(data.address)} size={16} testId="star-address" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="panel p-5">
          <div className="eyebrow mb-2">Balance</div>
          <div className="stat-value text-[26px]" style={{ color: "var(--amber)" }}>{fmtEth(data.balanceEth, 4)} <span className="text-[13px]" style={{ color: "var(--muted)" }}>{CHAIN.symbol}</span></div>
        </div>
        <div className="panel p-5">
          <div className="eyebrow mb-2">Transactions (nonce)</div>
          <div className="stat-value text-[26px]">{fmtNum(data.nonce)}</div>
        </div>
        <div className="panel p-5">
          <div className="eyebrow mb-2">Type</div>
          <div className="mt-1 flex gap-2 flex-wrap">
            {data.isValidator && <span className="pill pill-amber"><i className="fa-solid fa-shield-halved" /> Validator</span>}
            {data.isContract ? <span className="pill pill-green">Contract</span> : <span className="pill pill-muted">EOA Wallet</span>}
          </div>
        </div>
      </div>

      <Panel title="Overview" className="mb-5">
        <Row label="Address" mono>{data.address}<CopyBtn text={data.address} testId="copy-address" /></Row>
        <Row label="Balance">{fmtEth(data.balanceEth, 8)} {CHAIN.symbol}</Row>
        <Row label="Nonce">{fmtNum(data.nonce)}</Row>
        <Row label="Is Contract">{data.isContract ? `Yes (${fmtNum(data.codeSize)} bytes bytecode)` : "No"}</Row>
        <Row label="Is Validator">{data.isValidator ? "Yes — active QBFT validator" : "No"}</Row>
        <Row label="Public Name Tag">
          <span className="flex items-center gap-2 flex-wrap">
            {data.publicTag
              ? <span className="pill pill-amber"><i className="fa-solid fa-tag mr-1" />{data.publicTag}</span>
              : <span style={{ color: "var(--muted)" }}>—</span>}
            <button className="link-amber text-[12px]" onClick={() => setTagOpen((o) => !o)} data-testid="suggest-tag-btn">
              <i className="fa-solid fa-pen mr-1" />{data.publicTag ? "Request change" : "Suggest a tag"}
            </button>
          </span>
        </Row>
      </Panel>

      {/* Public name tag request (Etherscan-style: anyone suggests, admin approves) */}
      {tagOpen && (
        <Panel title="Suggest a Public Name Tag" subtitle="reviewed by admins" className="mb-5" testId="panel-suggest-tag">
          {tagMsg ? (
            <div className="p-5 text-center" data-testid="tag-request-success">
              <div style={{ color: "var(--green)" }}><i className="fa-solid fa-circle-check mr-1" /> Request submitted for review.</div>
              <div className="text-[12px] mt-1" style={{ color: "var(--muted)" }}>An admin will review your suggestion for <span className="mono">{shorten(data.address, 8, 6)}</span>. Approved tags are shown publicly.</div>
            </div>
          ) : (
            <form onSubmit={submitTag} className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="eyebrow block mb-1.5">Proposed Label *</label>
                  <input className="input-dark text-[12.5px]" value={tagLabel} onChange={(e) => setTagLabel(e.target.value)} placeholder='e.g. "Blokista: Faucet" or "Team Wallet"' data-testid="tag-request-label" />
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Contact (optional)</label>
                  <input className="input-dark text-[12.5px]" value={tagContact} onChange={(e) => setTagContact(e.target.value)} placeholder="email / handle" data-testid="tag-request-contact" />
                </div>
              </div>
              <label className="eyebrow block mb-1.5">Reason / Note (optional)</label>
              <input className="input-dark text-[12.5px] mb-3" value={tagNote} onChange={(e) => setTagNote(e.target.value)} placeholder="Why this label?" data-testid="tag-request-note" />
              <button type="submit" className="btn-amber w-full" disabled={tagBusy || !tagLabel.trim()} data-testid="tag-request-submit">
                {tagBusy ? "Submitting…" : <><i className="fa-solid fa-tag mr-1.5" /> Submit for Review</>}
              </button>
              {tagErr && <div className="mt-2 text-[12px] p-2 rounded" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="tag-request-error">{tagErr}</div>}
            </form>
          )}
        </Panel>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <button className={`nav-link ${tab === "txs" ? "active" : ""}`} onClick={() => setTab("txs")} data-testid="tab-address-txs"><i className="fa-solid fa-right-left mr-1.5" />Transactions</button>
        <button className={`nav-link ${tab === "tokens" ? "active" : ""}`} onClick={() => setTab("tokens")} data-testid="tab-address-tokens"><i className="fa-solid fa-coins mr-1.5" />Token Portfolio ({holdings.length})</button>
        {isVerifiedContract && (
          <button className={`nav-link ${tab === "contract" ? "active" : ""}`} onClick={() => setTab("contract")} data-testid="tab-address-contract"><i className="fa-solid fa-file-code mr-1.5" style={{ color: "var(--green)" }} />Contract</button>
        )}
      </div>

      {tab === "txs" ? (
        <Panel
          title="Transaction Activity"
          subtitle={txData?.scanComplete ? "full history" : "recent"}
          className="mb-5"
          bodyClass="overflow-auto"
          testId="panel-address-txs"
        >
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }} data-testid="activity-filters">
            <div className="flex items-center gap-1">
              {[["", "All"], ["in", "In"], ["out", "Out"], ["self", "Self"]].map(([v, l]) => (
                <button key={v || "all"} className={`nav-link ${dir === v ? "active" : ""}`} style={{ padding: "5px 12px" }}
                  onClick={() => resetAnd(() => setDir(v))} data-testid={`filter-dir-${v || "all"}`}>{l}</button>
              ))}
            </div>
            <select className="input-dark text-[12px]" style={{ width: 160 }} value={method}
              onChange={(e) => resetAnd(() => setMethod(e.target.value))} data-testid="filter-method">
              {METHODS.map((m) => <option key={m} value={m}>{m === "all" ? "All methods" : m}</option>)}
            </select>
            <select className="input-dark text-[12px]" style={{ width: 200 }} value={tokenFilter}
              onChange={(e) => resetAnd(() => setTokenFilter(e.target.value))} data-testid="filter-token">
              <option value="">All destinations</option>
              {tokenOpts.map((t) => <option key={t.address} value={t.address}>{t.symbol} · {shorten(t.address, 6, 4)}</option>)}
            </select>
            {(dir || method !== "all" || tokenFilter) && (
              <button className="link-amber text-[12px]" onClick={() => resetAnd(() => { setDir(""); setMethod("all"); setTokenFilter(""); })} data-testid="filter-clear">Clear filters</button>
            )}
          </div>

          <table className="dt">
            <thead><tr><th>Tx Hash</th><th>Method</th><th></th><th>From → To</th><th>Value</th><th>Block</th><th>Age</th></tr></thead>
            <tbody>
              {(txData?.transactions || []).map((t, i) => {
                const m = t.method || methodOf(t.input);
                return (
                  <tr key={t.hash} onClick={() => (window.location.href = `/tx/${t.hash}`)} data-testid={`address-tx-row-${i}`}>
                    <td><Link to={`/tx/${t.hash}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{shorten(t.hash, 8, 6)}</Link></td>
                    <td><span className={`pill ${methodClass(m)}`}>{m}</span></td>
                    <td><DirTag dir={t.direction} /></td>
                    <td className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>
                      <Link to={`/address/${t.from}`} className="link-amber" onClick={(e) => e.stopPropagation()}>{shorten(t.from, 5, 3)}</Link>
                      <i className="fa-solid fa-arrow-right-long mx-1.5" style={{ color: "var(--muted-2)" }} />
                      {t.to ? <Link to={`/address/${t.to}`} className="link-amber" onClick={(e) => e.stopPropagation()}>{shorten(t.to, 5, 3)}</Link> : <span className="pill pill-amber">Contract Creation</span>}
                    </td>
                    <td className="mono text-[12px]" style={{ color: "var(--text)" }}>{fmtEth(t.valueEth, 4)} {CHAIN.symbol}</td>
                    <td><Link to={`/block/${t.blockNumber}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>#{t.blockNumber}</Link></td>
                    <td className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>{timeAgo(t.timestamp)}</td>
                  </tr>
                );
              })}
              {txLoading && <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--muted)" }}>Scanning chain…</td></tr>}
              {!txLoading && (txData?.transactions || []).length === 0 && <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--muted)" }}>No transactions match{!txData?.scanComplete ? " in the recent block window" : ""}.</td></tr>}
            </tbody>
          </table>
          {(page > 1 || hasMore) && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button className="nav-link" disabled={page <= 1 || txLoading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="address-txs-prev"><i className="fa-solid fa-chevron-left mr-1" /> Prev</button>
              <span className="mono text-[12px]" style={{ color: "var(--muted)" }} data-testid="address-txs-page">Page {page}</span>
              <button className="nav-link" disabled={!hasMore || txLoading} onClick={() => setPage((p) => p + 1)} data-testid="address-txs-next">Next <i className="fa-solid fa-chevron-right ml-1" /></button>
            </div>
          )}
        </Panel>
      ) : tab === "tokens" ? (
        <Panel title="Token Portfolio" subtitle={`${holdings.length} assets`} className="mb-5" bodyClass="overflow-auto" testId="panel-portfolio">
          <table className="dt">
            <thead><tr><th>Token</th><th>Symbol</th><th>Contract</th><th>Balance</th></tr></thead>
            <tbody>
              {holdings.map((h, i) => (
                <tr key={h.address} onClick={() => (window.location.href = `/token/${h.address}`)} data-testid={`portfolio-row-${i}`}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <TokenLogo logo={h.logo} symbol={h.symbol} size={22} />
                      <span className="font-display font-semibold">{h.name}</span>
                      {h.verified && <VerifiedBadge small />}
                    </div>
                  </td>
                  <td><span className="pill pill-amber">{h.symbol}</span></td>
                  <td><Link to={`/token/${h.address}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{shorten(h.address, 8, 6)}</Link></td>
                  <td className="mono">{fmtNum(Math.round(h.balanceFmt))} {h.symbol}</td>
                </tr>
              ))}
              {holdings.length === 0 && <tr><td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>No BCC-20 token holdings.</td></tr>}
            </tbody>
          </table>
        </Panel>
      ) : (
        // Contract tab (verified) — Code / Read / Write
        <div className="mb-5">
          <div className="flex gap-2 mb-4">
            <button className={`nav-link ${ctab === "code" ? "active" : ""}`} onClick={() => setCtab("code")} data-testid="ctab-code">Source Code</button>
            <button className={`nav-link ${ctab === "read" ? "active" : ""}`} onClick={() => setCtab("read")} data-testid="ctab-read">Read Contract</button>
            <button className={`nav-link ${ctab === "write" ? "active" : ""}`} onClick={() => setCtab("write")} data-testid="ctab-write">Write Contract</button>
            <button className={`nav-link ${ctab === "events" ? "active" : ""}`} onClick={() => setCtab("events")} data-testid="ctab-events">Events</button>
          </div>

          {ctab === "events" ? (
            <Panel title="Event Logs" subtitle={events ? `${events.total} emitted` : ""} bodyClass="overflow-auto" testId="panel-contract-events">
              <table className="dt">
                <thead><tr><th>Event</th><th>Decoded Args</th><th>Block</th><th>Tx</th></tr></thead>
                <tbody>
                  {(events?.events || []).map((e, i) => (
                    <tr key={`${e.txHash}-${e.logIndex}`} data-testid={`event-row-${i}`}>
                      <td><span className="pill pill-amber">{e.event}</span></td>
                      <td className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>
                        {Object.entries(e.args).map(([k, v]) => (
                          <div key={k}><span style={{ color: "var(--muted-2)" }}>{k}:</span> {typeof v === "string" && v.startsWith("0x") && v.length === 42 ? <Link to={`/address/${v}`} className="link-amber" onClick={(ev) => ev.stopPropagation()}>{shorten(v, 6, 4)}</Link> : String(v)}</div>
                        ))}
                      </td>
                      <td><Link to={`/block/${e.block}`} className="link-amber mono text-[12px]">#{e.block}</Link></td>
                      <td><Link to={`/tx/${e.txHash}`} className="link-amber mono text-[12px]">{shorten(e.txHash, 6, 4)}</Link></td>
                    </tr>
                  ))}
                  {evLoading && <tr><td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>Decoding logs…</td></tr>}
                  {!evLoading && (events?.events || []).length === 0 && <tr><td colSpan={4} className="text-center py-8" style={{ color: "var(--muted)" }}>No events emitted yet.</td></tr>}
                </tbody>
              </table>
            </Panel>
          ) : ctab === "code" ? (
            <Panel title="Verified Source Code" subtitle={contract.contractName || ""} action={<VerifiedBadge />} testId="panel-address-source">
              <div className="p-4">
                <div className="mb-2 text-[11.5px]" style={{ color: "var(--muted)" }}>
                  Compiler <span className="mono">{contract.compilerVersion || "—"}</span> · Verified by <span className="mono">{contract.verifiedBy || "—"}</span>
                  {contract.bytecodeMatch && <span className="pill pill-green ml-2 text-[10px]"><i className="fa-solid fa-check" /> bytecode match</span>}
                </div>
                {contract.source
                  ? <pre className="ta-dark overflow-auto text-[12px] mono p-3 rounded-lg" style={{ maxHeight: 520, whiteSpace: "pre" }} data-testid="address-source-viewer">{contract.source}</pre>
                  : <div className="text-center py-6" style={{ color: "var(--muted)" }}>No source stored.</div>}
              </div>
            </Panel>
          ) : contract.abi ? (
            ctab === "read"
              ? <ContractConsole address={data.address} abi={contract.abi} mode="read" />
              : <ContractConsole address={data.address} abi={contract.abi} mode="write" />
          ) : (
            <Panel title={ctab === "read" ? "Read Contract" : "Write Contract"} testId="panel-no-abi">
              <div className="p-8 text-center" style={{ color: "var(--muted)" }} data-testid="contract-no-abi">
                The ABI for this contract isn't available. Re-verify its source in the Contracts studio to enable read &amp; write.
              </div>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
